import path from 'path';
import { PulseCdnConnectDto } from '../../account/store/services/storages/PulseCdnStorage';
import S3File from '../../products/edit/files/store/S3File';
import { isUrlPointsToFile } from '../../products/store/utils';
import type { CdnLocation, PulseCdnBrowser, PulseCdnOptions } from '../types';
import loadAssets from './loadAssets';

const wnd = window as {
  BucketBlazeConfig?: PulseCdnOptions;
  BucketBlazeFileBrowser?: PulseCdnFileBrowserOriginal;
} & Window;

export default async function loadPulseCdn({
  getOptions,
  initialPath = '/',
  pulseCdnOptions,
}: {
  getOptions: () => Promise<PulseCdnConnectDto>;
  initialPath?: string;
  pulseCdnOptions?: PulseCdnOptions;
}): Promise<PulseCdnBrowser> {
  // bbBrowser.getCurrentPath() always returns '/'
  let localLocation = initialPath;

  const options = await getOptions();
  const { productInfo, ...originalOptions } = pulseCdnOptions || {};

  const isFile = isUrlPointsToFile(initialPath);
  if (isFile) {
    localLocation = urlDirName(initialPath);
  }

  const showSetupWizard = Boolean(
    productInfo?.isNew &&
    productInfo.name &&
    productInfo.name !== sessionStorage.lastProductName,
  );
  if (showSetupWizard) {
    sessionStorage.lastProductName = productInfo?.name;
  }

  const initialConfig: PulseCdnOptions = {
    apiToken: options.apiToken,
    apiDomain: options.apiBaseUrl,
    fileStorageId: options.storageId,
    fileStorageRootUrl: options.cdnBaseUrl,
    fileSigningUrl: `${options.apiBaseUrl}/api/sign`,
    initialPath: normalizePath(localLocation),
    preSelectedFiles: isFile ? path.basename(initialPath) : '',
    productInfo,
    productName: showSetupWizard ? productInfo?.name : undefined,
    showSetupWizard,

    ...originalOptions,
  };

  if (!wnd.BucketBlazeConfig) {
    wnd.BucketBlazeConfig = initialConfig;
  }

  await timeout({
    promise: loadAssets([
      {
        id: 'pulsecdn-file-browser-js',
        url: `${options.apiBaseUrl}/pulse/assets/index.js`,
      },
      {
        id: 'pulsecdn-file-browser-css',
        url: `${options.apiBaseUrl}/pulse/assets/index.css`,
      },
    ]),
  });

  const cdnBrowser = await timeout({
    promise: new Promise<PulseCdnFileBrowserOriginal>((resolve) => {
      const intervalId = setInterval(() => {
        if (typeof wnd.BucketBlazeFileBrowser === 'object') {
          clearInterval(intervalId);
          resolve(wnd.BucketBlazeFileBrowser);
        }
      }, 50);
    }),
    timeoutMs: 30_000,
  });

  cdnBrowser.on('locationchange', (data) => {
    localLocation = data.path;
  });

  // The CDN doesn't reliably open a nested initialPath from config on load, so
  // drive it explicitly (config.preSelectedFiles then selects the file).
  if (normalizePath(localLocation) !== '/') {
    cdnBrowser.navigate(normalizePath(localLocation));
  }

  const cdnBaseUrl = options.cdnBaseUrl.replace(/([^/])$/, '$1/');

  return {
    getFileUrl,

    getSelectedFiles(): S3File[] {
      return cdnBrowser
        .getSelectedFiles()
        .map((fileName) => S3File.fromUrl(getFileUrl(fileName)));
    },

    navigate(urlOrPath: string) {
      if (urlOrPath === 'https://pulsecdn.net/') {
        cdnBrowser.navigate('/');
        return;
      }

      cdnBrowser.navigate(normalizePath(extractCdnPath(urlOrPath)));
    },

    onLocationChange(listener: (location: CdnLocation) => void) {
      cdnBrowser.on('locationchange', (data) => {
        listener({ path: normalizePath(data.path), url: getDirUrl(data.path) });
      });
    },

    onSelectionChange(listener: (selected: S3File[]) => void) {
      cdnBrowser.on('selectionchange', (data) => {
        listener(data.selected.map((f) => S3File.fromUrl(getFileUrl(f.name))));
      });
    },

    onDialog(listener: (shown: boolean) => void) {
      cdnBrowser.on('dialog', (data) => listener(data.shown));
    },

    onUpload(listener: (inProgress: boolean) => void) {
      cdnBrowser.on('upload', (data) => listener(data.inProgress));
    },

    setSelectedFiles(files: S3File[]) {
      cdnBrowser.setSelectedFiles(files.map((file) => path.basename(file.url)));
    },
  };

  function getFileUrl(fileNameOrUrl: string) {
    if (isFullUrl(fileNameOrUrl)) {
      return fileNameOrUrl;
    }

    const pathname = path.join(localLocation, fileNameOrUrl).replace(/^\//, '');
    return new URL(pathname, cdnBaseUrl).href;
  }

  function getDirUrl(dirPath: string) {
    const normalized = dirPath.replace(/^\//, '').replace(/([^/])$/, '$1/');
    return new URL(normalized, cdnBaseUrl).href;
  }

  function extractCdnPath(urlOrPath: string): string {
    if (!isFullUrl(urlOrPath)) {
      return urlOrPath;
    }

    try {
      const url = new URL(urlOrPath);
      const base = new URL(cdnBaseUrl);
      if (
        url.origin === base.origin &&
        url.pathname.startsWith(base.pathname)
      ) {
        const rawPath = url.pathname.slice(base.pathname.length - 1) || '/';
        return decodeURIComponent(rawPath.replaceAll('+', '%20'));
      }
    } catch {
      // not a valid URL, treat as path
    }

    return urlOrPath;
  }
}

function normalizePath(sourcePath: string) {
  const withLeadingSlash = sourcePath.startsWith('/')
    ? sourcePath
    : `/${sourcePath}`;
  return withLeadingSlash.length > 1
    ? withLeadingSlash.replace(/\/+$/, '')
    : withLeadingSlash;
}

async function timeout<T = void>({
  errorMessage = "Can't load PulseCDN File Browser",
  promise,
  timeoutMs = 30_000,
}: {
  errorMessage?: string;
  promise: Promise<T>;
  timeoutMs?: number;
}): Promise<T> {
  const timeoutText = `in ${(timeoutMs / 1000).toFixed(1)} seconds`;
  return Promise.race([
    promise,
    new Promise<void>((_, reject) => {
      setTimeout(() => {
        let errorWithTimeout = errorMessage;
        if (!errorWithTimeout.includes('{timeout}')) {
          errorWithTimeout = `${errorMessage} {timeout}.`;
        }

        reject(new Error(errorWithTimeout.replace('{timeout}', timeoutText)));
      }, timeoutMs);
    }),
  ]) as Promise<T>;
}

function isFullUrl(value: string): boolean {
  return /^https?:\/\//.test(value);
}

function urlDirName(url: string) {
  let initialPath = url;
  try {
    initialPath = new URL(url).pathname;
  } catch {
    // not a valid URL, treat as a path
  }

  return initialPath.split('/').slice(0, -1).join('/');
}

interface PulseCdnEventsMap {
  dialog: { shown: boolean };
  locationchange: { path: string };
  selectionchange: { selected: CdnFile[] };
  upload: { inProgress: boolean };
}

interface CdnFile {
  id: string;
  modifiedAt: string;
  name: string;
  parentPath: string;
  size: number;
  type: string;
}

interface PulseCdnFileBrowserOriginal {
  getCurrentPath(): string;
  getSelectedFiles(): string[];
  navigate(path: string): void;
  off<Event extends keyof PulseCdnEventsMap>(
    event: Event,
    listener: (data: PulseCdnEventsMap[Event]) => void,
  ): void;
  on<Event extends keyof PulseCdnEventsMap>(
    event: Event,
    listener: (data: PulseCdnEventsMap[Event]) => void,
  ): void;
  removeEventListener(): void;
  setSelectedFiles(files: string[]): void;
}
